import React, { useEffect } from 'react'
import PropTypes from 'prop-types'
import { useForm } from 'react-hook-form'
import { XIcon } from '@heroicons/react/outline'

import { Form, FormField, FormItem, FormLabel, FormControl, FormDescription, FormMessage } from '../../ui/form'
import { Input } from '../../ui/input'
import { Textarea } from '../../ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select'

function EditDisclosureModal({ isOpen, onClose, disclosure, onSave, isLoading }) {
  const hasResponses = disclosure && disclosure.responseCount > 0

  const form = useForm({
    defaultValues: {
      name: disclosure?.name || '',
      disclosureType: disclosure?.disclosureType || 'one_liner',
      agreement: disclosure?.agreement || ''
    }
  })

  const { handleSubmit, reset, watch } = form
  const disclosureType = watch('disclosureType')
  const agreementText = watch('agreement')

  useEffect(() => {
    if (disclosure) {
      reset({
        name: disclosure.name || '',
        disclosureType: disclosure.disclosureType || 'one_liner',
        agreement: disclosure.agreement || ''
      })
    }
  }, [disclosure, reset])

  function onSubmit(data) {
    onSave(disclosure.id, data)
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
      <div className="flex items-end justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div
          className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
          aria-hidden="true"
          onClick={onClose}
        ></div>

        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
          &#8203;
        </span>

        <div className="inline-block overflow-hidden text-left align-bottom transition-all transform bg-white rounded-lg shadow-xl sm:my-8 sm:align-middle sm:max-w-3xl sm:w-full">
          <div className="absolute top-0 right-0 pt-4 pr-4">
            <button
              type="button"
              className="text-gray-400 bg-white rounded-md hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
              onClick={onClose}
            >
              <span className="sr-only">Close</span>
              <XIcon className="w-6 h-6" />
            </button>
          </div>

          <Form {...form}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="px-4 pt-5 pb-4 bg-white sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="w-full mt-3 text-center sm:mt-0 sm:text-left">
                    <h3 className="text-lg font-medium leading-6 text-gray-900" id="modal-title">
                      Edit Disclosure
                    </h3>

                    {hasResponses && (
                      <div className="p-3 mt-4 text-sm text-purple-700 bg-purple-100 rounded-md">
                        This disclosure has {disclosure.responseCount} response(s). The disclosure type and agreement
                        text cannot be changed once responses exist.
                      </div>
                    )}

                    <div className="mt-6 space-y-6">
                      <FormField
                        control={form.control}
                        name="name"
                        rules={{ required: 'Name is required' }}
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Name</FormLabel>
                            <FormControl>
                              <Input placeholder="Enter disclosure name" disabled={isLoading} {...field} />
                            </FormControl>
                            <FormDescription>For your reference. Not shown to registrants.</FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="disclosureType"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Type</FormLabel>
                            <Select
                              onValueChange={field.onChange}
                              defaultValue={field.value}
                              disabled={isLoading || hasResponses}
                            >
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue placeholder="Select type" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                <SelectItem value="one_liner">One-liner</SelectItem>
                                <SelectItem value="document">Document</SelectItem>
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="agreement"
                        rules={{ required: 'Agreement text is required' }}
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Agreement</FormLabel>
                            <FormControl>
                              <Textarea
                                placeholder="I agree to..."
                                rows={disclosureType === 'document' ? 10 : 2}
                                maxLength={disclosureType === 'one_liner' ? 140 : undefined}
                                disabled={isLoading || hasResponses}
                                {...field}
                              />
                            </FormControl>
                            <FormDescription>This is what the registrant accepts.</FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <div className="p-4 bg-gray-50 rounded-md">
                        <h4 className="mb-2 text-sm font-medium text-gray-900">Preview</h4>
                        <p className="text-sm text-gray-600">
                          {agreementText || 'Your agreement text will appear here...'}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="px-4 py-3 bg-gray-50 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="submit"
                  disabled={isLoading}
                  className="inline-flex items-center justify-center w-full px-4 py-2 text-sm font-medium text-white bg-purple-600 border border-transparent rounded-md sm:ml-3 sm:w-auto hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50"
                >
                  {isLoading ? (
                    <>
                      <svg
                        className="w-5 h-5 mr-2 -ml-1 text-white animate-spin"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      Saving...
                    </>
                  ) : (
                    'Save Changes'
                  )}
                </button>
                <button
                  type="button"
                  onClick={onClose}
                  disabled={isLoading}
                  className="inline-flex items-center justify-center w-full px-4 py-2 mt-3 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md sm:mt-0 sm:w-auto hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50"
                >
                  Cancel
                </button>
              </div>
            </form>
          </Form>
        </div>
      </div>
    </div>
  )
}

EditDisclosureModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  disclosure: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    name: PropTypes.string,
    disclosureType: PropTypes.string,
    agreement: PropTypes.string,
    responseCount: PropTypes.number
  }),
  onSave: PropTypes.func.isRequired,
  isLoading: PropTypes.bool
}

export default EditDisclosureModal
